import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import theme from '../../../theme/theme';
import { usePermissions } from '../../permissions/usePermissions';
import { renderCell } from '../../../components/ui/tableTemplate/cellRenderers';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import type { RightsProfile } from '../types/rights-profile.types';

interface UseRightsProfileColumnsOptions {
  onEdit: (profile: RightsProfile) => void;
  onDelete: (profile: RightsProfile) => void;
}

/**
 * Columns for the rights-profiles list table (Name / Description / Users / Actions).
 * Left-aligned, Name in bold; the Users cell is a soft badge with a leading dot —
 * grey "Aucun" when no user is assigned, blue "N utilisateurs" otherwise. The
 * Actions cell holds the Edit (primary) and Delete row buttons, separated by a
 * divider (mockup « Profils de droits », écran de liste).
 */
export function useRightsProfileColumns({
  onEdit,
  onDelete,
}: UseRightsProfileColumnsOptions): TableColumn<RightsProfile>[] {
  const { t } = useTranslation('rightsProfiles');
  const { can } = usePermissions();
  const canDelete = can('rights_profiles', 'delete');

  return useMemo<TableColumn<RightsProfile>[]>(
    () => [
      {
        key: 'name',
        labelKey: 'rightsProfiles:list.columns.name',
        width: '22%',
        // Sorted by the server (HOMCO-1413), like assigned_users_count below.
        sortable: true,
        // Bold name + a "Locked" badge on the system profile (HOMCO-1225).
        render: profile => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              component="span"
              sx={{ fontSize: '0.85rem', fontWeight: 600, color: theme.palette.text.body }}
            >
              {profile.name}
            </Typography>
            {profile.is_locked && (
              <Chip
                size="small"
                icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                label={t('list.lockedBadge')}
                sx={{
                  height: 20,
                  bgcolor: theme.palette.grey[200],
                  color: theme.palette.text.body,
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: theme.palette.text.subtle },
                }}
              />
            )}
          </Stack>
        ),
      },
      {
        key: 'description',
        labelKey: 'rightsProfiles:list.columns.description',
        width: 320,
        render: profile => renderCell('text', profile.description ?? '—', { t }),
      },
      {
        key: 'assigned_users_count',
        labelKey: 'rightsProfiles:list.columns.users',
        width: 100,
        sortable: true,
        render: profile => {
          const count = profile.assigned_users_count;
          const assigned = count > 0;
          const accent = assigned ? theme.palette.primary.main : theme.palette.text.subtle;
          return (
            <Chip
              size="small"
              variant="filled"
              label={
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Box
                    component="span"
                    sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: accent }}
                  />
                  {assigned ? t('list.usersCell.count', { count }) : t('list.usersCell.none')}
                </Box>
              }
              sx={{
                bgcolor: assigned
                  ? alpha(theme.palette.primary.main, 0.12)
                  : theme.palette.grey[200],
                color: theme.palette.text.body,
                fontWeight: 500,
                border: 'none',
              }}
            />
          );
        },
      },
      {
        key: 'id',
        labelKey: '',
        width: 100,
        align: 'right',
        stickyRight: true,
        render: profile => (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Tooltip title={t('list.rowActions.edit')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('list.rowActions.edit')}
                onClick={event => {
                  event.stopPropagation();
                  onEdit(profile);
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Delete is gated by rights_profiles.delete (the backend refuses it
                otherwise). A locked (system) profile cannot be deleted either
                (HOMCO-1225): disabled with an explicit tooltip. */}
            {!canDelete ? null : profile.is_locked ? (
              <Tooltip title={t('list.lockedCannotDelete')}>
                <span>
                  <IconButton size="small" aria-label={t('list.rowActions.delete')} disabled>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={t('list.rowActions.delete')}>
                <IconButton
                  size="small"
                  aria-label={t('list.rowActions.delete')}
                  onClick={event => {
                    event.stopPropagation();
                    onDelete(profile);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [t, onEdit, onDelete, canDelete],
  );
}
